import { useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { movies } from '@/mocks/movies';
import MovieCard from '@/components/feature/MovieCard';
import EmptyState from '@/components/base/EmptyState';
import { useFavorites } from '@/hooks/useFavorites';
import { usePageMeta } from '@/hooks/usePageMeta';

export default function MovieDetail() {
  const { id } = useParams<{ id: string }>();
  const movie = movies.find((item) => item.id === id);
  const { isFavorite, toggleFavorite } = useFavorites();
  const [toast, setToast] = useState('');
  const toastTimer = useRef<number | null>(null);

  usePageMeta(
    movie ? `${movie.title} - 影视详情 - 青苹果影院` : '影片不存在 - 青苹果影院',
    movie
      ? `${movie.title}（${movie.year}，${movie.region}）：${movie.synopsis.slice(0, 60)}……查看导演、主演、类型与相关推荐。`
      : '该影片可能已下架，返回首页继续浏览其他影视作品。',
    movie ? `${movie.title},${movie.genres.join(',')},影视详情,青苹果影院` : '青苹果影院',
  );

  useEffect(() => {
    return () => {
      if (toastTimer.current) window.clearTimeout(toastTimer.current);
    };
  }, []);

  const showToast = (message: string) => {
    setToast(message);
    if (toastTimer.current) window.clearTimeout(toastTimer.current);
    toastTimer.current = window.setTimeout(() => setToast(''), 2200);
  };

  if (!movie) {
    return (
      <div className="w-full">
        <div className="max-w-[1440px] mx-auto px-4 md:px-8 py-16 md:py-24">
          <EmptyState
            icon="ri-film-line"
            title="影片不存在或已下架"
            description="你访问的影片可能已被移除，或者链接有误。可以返回首页继续浏览其他作品。"
            actionLabel="返回首页"
            actionTo="/"
          />
        </div>
      </div>
    );
  }

  const favorited = isFavorite(movie.id);

  const related = movies
    .filter((item) => item.id !== movie.id)
    .map((item) => ({
      movie: item,
      score: item.genres.filter((genre) => movie.genres.includes(genre)).length * 2 + (item.category === movie.category ? 1 : 0),
    }))
    .filter((entry) => entry.score > 0)
    .sort((a, b) => b.score - a.score || b.movie.rating - a.movie.rating)
    .slice(0, 6)
    .map((entry) => entry.movie);

  const metaItems = [
    { label: '导演', value: movie.director },
    { label: '主演', value: movie.cast.join(' / ') },
    { label: '地区', value: movie.region },
    { label: '年份', value: `${movie.year}` },
    { label: '片长', value: movie.duration },
    { label: '上线日期', value: movie.releaseDate },
  ];

  return (
    <div className="w-full">
      <div className="w-full border-b border-background-200 bg-background-100">
        <div className="max-w-[1440px] mx-auto px-4 md:px-8 h-11 flex items-center gap-2 text-xs text-foreground-500 overflow-hidden">
          <Link to="/" className="hover:text-primary-700 transition-colors whitespace-nowrap">
            首页
          </Link>
          <i className="ri-arrow-right-s-line text-sm shrink-0" aria-hidden="true" />
          <Link to={`/category?type=${movie.category}`} className="hover:text-primary-700 transition-colors whitespace-nowrap">
            {movie.category === 'movie' ? '电影分类' : '电视剧分类'}
          </Link>
          <i className="ri-arrow-right-s-line text-sm shrink-0" aria-hidden="true" />
          <span className="text-foreground-800 truncate">{movie.title}</span>
        </div>
      </div>

      <section className="w-full border-b border-background-200 bg-background-100/60">
        <div className="max-w-[1440px] mx-auto px-4 md:px-8 py-8 md:py-10">
          <div className="flex flex-col md:flex-row gap-6 md:gap-10">
            <div className="w-[180px] sm:w-[220px] md:w-[250px] shrink-0 mx-auto md:mx-0">
              <div className="relative w-full aspect-[2/3] rounded-lg overflow-hidden border border-background-300/60 bg-background-200">
                <img
                  src={movie.poster}
                  alt={`${movie.title}海报`}
                  title={`${movie.title} - 影视详情 - 青苹果影院`}
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="mt-3 flex items-center justify-between px-1">
                <span className="text-xs text-foreground-500">封面</span>
                <span className="text-xs text-foreground-500">演示资料</span>
              </div>
            </div>

            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <span className="px-2 py-0.5 rounded-md bg-primary-100 text-primary-800 text-xs font-medium">
                  {movie.category === 'movie' ? '电影' : '电视剧'}
                </span>
                {movie.isNew && (
                  <span className="px-2 py-0.5 rounded-md bg-accent-500 text-background-50 text-xs font-medium">新上线</span>
                )}
                {movie.isHot && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-accent-100 text-accent-800 text-xs font-medium">
                    <i className="ri-fire-line text-xs" aria-hidden="true" />
                    热门
                  </span>
                )}
              </div>

              <h1 className="mt-3 text-2xl md:text-4xl font-semibold text-foreground-950 tracking-tight leading-tight">
                {movie.title}
              </h1>

              <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2">
                <span className="flex items-center gap-1.5 text-2xl font-semibold text-accent-600 font-label">
                  <i className="ri-star-fill text-lg" aria-hidden="true" />
                  {movie.rating.toFixed(1)}
                </span>
                <span className="text-sm text-foreground-500">综合评分 · {movie.popularity} 人关注</span>
                <span className="inline-flex items-center gap-1 text-sm text-foreground-600">
                  <i className="ri-time-line text-base" aria-hidden="true" />
                  {movie.duration}
                </span>
              </div>

              <div className="mt-3 flex flex-wrap gap-2">
                {movie.genres.map((genre) => (
                  <Link
                    key={genre}
                    to={`/category?genres=${encodeURIComponent(genre)}`}
                    className="px-2.5 py-1 rounded-md bg-background-50 border border-background-200 text-xs text-foreground-700 hover:border-primary-300 hover:text-primary-700 transition-colors"
                  >
                    {genre}
                  </Link>
                ))}
              </div>

              <dl className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2.5 max-w-2xl">
                {metaItems.map((item) => (
                  <div key={item.label} className="flex items-start gap-2 text-sm">
                    <dt className="w-14 shrink-0 text-foreground-500">{item.label}</dt>
                    <dd className="text-foreground-800 min-w-0 break-words">{item.value}</dd>
                  </div>
                ))}
              </dl>

              <div className="mt-6 flex flex-wrap items-center gap-3">
                <button
                  type="button"
                  onClick={() => {
                    toggleFavorite(movie.id);
                    showToast(favorited ? '已取消收藏' : '已收藏到本地');
                  }}
                  aria-pressed={favorited}
                  className={`inline-flex items-center gap-2 h-10 px-5 rounded-md text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                    favorited
                      ? 'bg-accent-500 text-foreground-950 hover:bg-accent-400'
                      : 'border border-background-300 text-foreground-800 hover:border-accent-400 hover:text-accent-700'
                  }`}
                >
                  <i className={favorited ? 'ri-heart-fill' : 'ri-heart-line'} aria-hidden="true" />
                  {favorited ? '已收藏' : '收藏影片'}
                </button>
                <Link
                  to="/category"
                  className="inline-flex items-center gap-2 h-10 px-4 rounded-md border border-background-300 text-sm text-foreground-700 hover:border-primary-300 hover:text-primary-700 transition-colors whitespace-nowrap"
                >
                  <i className="ri-arrow-left-line" aria-hidden="true" />
                  返回分类
                </Link>
                <span className="text-xs text-foreground-400">收藏仅保存在当前浏览器本地</span>
              </div>

              {toast && (
                <div className="mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-primary-50 border border-primary-200 text-xs text-primary-800 animate-fade-in">
                  <i className="ri-check-line" aria-hidden="true" />
                  {toast}
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      <div className="max-w-[1440px] mx-auto px-4 md:px-8 pt-8 md:pt-10 pb-14 md:pb-16">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6">
          <article className="lg:col-span-2 rounded-lg border border-background-200 bg-background-50 p-5 md:p-7">
            <div className="flex items-center gap-2">
              <span className="w-1 h-4 rounded-full bg-primary-500" aria-hidden="true" />
              <h2 className="text-lg md:text-xl font-semibold text-foreground-950">剧情简介</h2>
            </div>
            <p className="mt-4 text-sm md:text-base text-foreground-700 leading-[1.9]">{movie.synopsis}</p>
            <p className="mt-4 text-sm text-foreground-600 leading-[1.9]">
              本片由 {movie.director} 执导，{movie.cast.join('、')} 主演，{movie.year} 年在{movie.region}上线。
              故事围绕人物在特定处境下的选择展开，节奏克制，适合安静地看完。以上资料为本站整理的演示内容。
            </p>

            <div className="mt-6 pt-5 border-t border-background-200 grid grid-cols-2 sm:grid-cols-4 gap-4">
              {[
                { icon: 'ri-user-star-line', label: '导演', value: movie.director },
                { icon: 'ri-group-line', label: '主演', value: `${movie.cast.length} 位` },
                { icon: 'ri-price-tag-3-line', label: '题材', value: movie.genres[0] },
                { icon: 'ri-calendar-line', label: '上线', value: movie.releaseDate },
              ].map((item) => (
                <div key={item.label} className="flex items-start gap-2">
                  <span className="w-8 h-8 shrink-0 rounded-md bg-background-100 flex items-center justify-center text-primary-600">
                    <i className={`${item.icon} text-base`} aria-hidden="true" />
                  </span>
                  <span className="min-w-0">
                    <span className="block text-[11px] text-foreground-500">{item.label}</span>
                    <span className="block text-xs md:text-sm text-foreground-800 truncate">{item.value}</span>
                  </span>
                </div>
              ))}
            </div>
          </article>

          <aside className="rounded-lg border border-background-200 bg-secondary-100/60 p-5 md:p-6">
            <h2 className="text-base md:text-lg font-semibold text-foreground-950">观影小记</h2>
            <p className="mt-3 text-sm text-foreground-600 leading-relaxed">
              这是一部适合在晚上慢慢看的作品。留意声音与光线的变化，很多情绪并不靠台词表达。
            </p>
            <div className="mt-5 space-y-3">
              {[
                { icon: 'ri-moon-clear-line', label: '推荐时段', value: '夜晚 · 安静环境' },
                { icon: 'ri-emotion-happy-line', label: '适合心情', value: '想被故事带走时' },
                { icon: 'ri-user-3-line', label: '适合同伴', value: '独自或两三好友' },
              ].map((item) => (
                <div key={item.label} className="flex items-center gap-3">
                  <span className="w-8 h-8 shrink-0 rounded-md bg-background-50 flex items-center justify-center text-accent-600">
                    <i className={`${item.icon} text-base`} aria-hidden="true" />
                  </span>
                  <div className="min-w-0">
                    <p className="text-[11px] text-foreground-500">{item.label}</p>
                    <p className="text-xs md:text-sm text-foreground-800">{item.value}</p>
                  </div>
                </div>
              ))}
            </div>
            <p className="mt-5 pt-4 border-t border-secondary-200 text-[11px] text-foreground-500 leading-relaxed">
              本站仅提供影视资料展示，不提供播放与下载服务。
            </p>
          </aside>
        </div>

        <section className="mt-10 md:mt-14">
          <div className="flex items-end justify-between gap-4 mb-4 md:mb-6">
            <div>
              <h2 className="text-lg md:text-xl font-semibold text-foreground-950">相关推荐</h2>
              <p className="mt-1 text-xs md:text-sm text-foreground-500">根据题材标签匹配的相似作品</p>
            </div>
            <Link
              to={`/category?genres=${encodeURIComponent(movie.genres[0])}`}
              className="inline-flex items-center gap-1 h-9 px-3.5 rounded-md border border-background-200 text-sm text-foreground-700 hover:border-primary-300 hover:text-primary-700 transition-colors whitespace-nowrap"
            >
              更多{movie.genres[0]}
              <i className="ri-arrow-right-line text-base" aria-hidden="true" />
            </Link>
          </div>

          {related.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-x-3 gap-y-6 md:gap-x-4 md:gap-y-8">
              {related.map((item) => (
                <MovieCard key={item.id} movie={item} />
              ))}
            </div>
          ) : (
            <EmptyState
              icon="ri-compass-3-line"
              title="暂无相关推荐"
              description="换一个题材看看，或回到分类页浏览全部影片。"
              actionLabel="浏览全部影片"
              actionTo="/category"
            />
          )}
        </section>
      </div>
    </div>
  );
}